import { pick } from 'lodash'
import moment from 'moment'

import lang from '@/lang'
import config from 'config'
import todoConfig from '@/modules/todo/config'
import TodoStatusEnum from '@/enums/TodoStatus.enum'
import { AddToCounter, updateItem } from '@/redux/ducks/todo/list'
import store from '@/redux/store'
import { user } from 'ampersand-app'
import request from '@/modules/base/util/request'
import handleUploadError from '@/modules/file/_helpers/handleUploadError'
import { FineUploaderBasic as Uploader } from 'fine-uploader'
import FileService from '@/modules/file/fileService'
import todoTypesEnum from '@/enums/todo-types.enum'

const { resourceHost, dateFormat: { api: apiDateFormat } } = config

/**
 * @readonly
 * @enum {string}
 */
const TODO_FILTER = Object.freeze({
  ALL: 'all',
  DUE: 'due',
  MY: 'my',
})

const TYPE_USER = 'user'
const TYPE_FILE = 'file'
const TYPE_SUPPLIER = 'supplier'
const TYPE_CUSTOMER = 'customer'
const TYPE_DOCUMENT = 'todoDocument'

const regExExpression = /\[\[(.*?)\]\]/
const sanitizeContent = (content) => {
  return content
    ? content.split(regExExpression).reduce((result, token) => {
      if (token.startsWith('{')) {
        token = pick(JSON.parse(token), ['type', 'value', 'label', 'id', 'prefix'])
        token = `[[${JSON.stringify(token)}]]`
      }
      return result + token
    }, '')
    : ''
}

const uploadDocuments = async(file, todoType) => {
  return new Promise((resolve, reject) => {
    const uploader = new Uploader({
      autoUpload: false,
      multiple: false,
      request: {
        endpoint: todoConfig.uploadUrl,
        customHeaders: { authorization: `Bearer ${user?.token}` },
        inputName: 'file',
      },
      callbacks: {
        onError: (id, name, reason, xhr) => {
          const parsedJson = (xhr && JSON.parse(xhr.response)) || null
          reject(parsedJson)
        },
        onComplete: (id, name, response, xhr) => resolve({ body: response }),
      },
    })
    const params = { filename: file.name }
    if (todoType) {
      params.todoType = todoType
    }

    uploader.setParams(params)
    uploader.addBlobs([{ name: file.name, blob: file }])
    uploader.uploadStoredFiles()
  })
}

/**
 * @typedef {Object} Todo
 * @property {string} content
 * @property {string} dueToDate
 * @property {int[]|string[]} [userIds]
 * @property {int[]|string[]} [supplierIds]
 * @property {int[]|string[]} [customerIds]
 */

/**
 * @typedef {Object} GetOptions
 * @property {number} [offset]
 * @property {number} [limit]
 * @property {string} [searchTerm]
 * @property {TODO_FILTER} [activeFilter]
 */
const TodoService = {
  parseRelations: (content) => {
    const relations = content.split(regExExpression)
      .filter(s => s !== null && s.startsWith('{'))
      .map(item => {
        try {
          return JSON.parse(item)
        } catch {
        }
        return null

      })
    return relations
  },
  /**
   * @param {Todo} todo
   */
  create: (todo) => {
    return new Promise((resolve, reject) => {
      request(`${resourceHost}todo`, {
        method: 'POST',
        auth: true,
        isConcurrent: true,
        data: { ...todo, content: sanitizeContent(todo.content) },
      })
        .then(response => {
          const { body: { data } } = response
          resolve(data)
        })
        .catch(reject)
    })
  },

  /**
   * @param {Todo} todo
   */
  edit: (todo) => {
    return new Promise((resolve, reject) => {
      const { id } = todo
      request(`${resourceHost}todo/${id}`, {
        method: 'PUT',
        auth: true,
        isConcurrent: true,
        data: { ...todo, content: sanitizeContent(todo.content) },
      })
        .then(response => {
          const { body: { data } } = response
          store.dispatch(updateItem(data))
          resolve(data)
        })
        .catch(reject)
    })
  },

  /**
   * @param {string} id
   */
  delete: (id) => {
    return new Promise((resolve, reject) => {
      request(`${resourceHost}todo/${id}`, {
        method: 'DELETE',
        auth: true,
        isConcurrent: true,
      })
        .then(resolve)
        .catch(reject)
    })
  },

  getById: (id) => {
    return new Promise((resolve, reject) => {
      request(`${resourceHost}todo/${id}`, {
        auth: true,
        isConcurrent: true,
      })
        .then(response => {
          const { body: { data } } = response
          resolve(data)
        })
        .catch((error) => reject(error))
    })
  },

  /**
   * @param {GetOptions} options
   */
  getAll: (options) => {
    const { offset = 0, limit, searchTerm, activeFilter, orderBy, collaborationId } = options || {}
    const finalOffset = searchTerm ? 0 : offset
    const data = {
      collaborationId,
      searchTerm,
      startRow: finalOffset,
      endRow: limit ? finalOffset + limit : undefined,
      sortModel: [{ sort: 'asc', colId: orderBy || 'createdAt' }],
      filterItems: [
        {
          name: 'my',
          quickFilter: {
            // The server resolves this to the authenticated user — it never trusts a
            // value from the request body for who "responsible" means.
            responsibleId: {
              type: 'currentUserResponsible',
            },
          },
        },
        {
          // from is today or older and to date is today or in the future
          name: 'due',
          quickFilter: {
            status: {
              type: 'set',
              values: [
                'open',
              ],
            },
            dueFromDate: {
              filterType: 'datetime',
              type: 'lessThanOrEqual',
              dateFrom: moment().endOf('day'),
            },
            dueToDate: {
              filterType: 'datetime',
              type: 'greaterThanOrEqual',
              dateFrom: moment().startOf('day').toISOString(),
            },
          },
        },
      ],
    }
    if (activeFilter) {
      data.filterModel = data.filterItems.find(filter => filter.name === activeFilter)
    }
    return new Promise((resolve, reject) => {
      return request(todoConfig.routes.list, { method: 'POST', auth: true, isConcurrent: true, data })
        .then(response => {
          const { body: { data, meta } } = response
          resolve({
            data,
            meta: {
              all: meta.totalCount,
              my: meta.my,
              due: meta.due,
              overdue: meta.overdue,
            },
          })
        })
        .catch(reject)
    })
  },

  getMessages: (todoId, options) => {
    const { offset = 0, limit = -1 } = options || {}

    return new Promise((resolve, reject) => {
      const url = `${resourceHost}todo/${todoId}/messages?offset=${offset}&limit=${limit}`
      return request(url, { auth: true, isConcurrent: true })
        .then(response => {
          const { body } = response
          resolve(body)
        })
        .catch(reject)
    })
  },

  createMessage: (todoId, message) => {
    return new Promise((resolve, reject) => {
      const url = `${resourceHost}todo/${todoId}/message`
      return request(url, { auth: true, isConcurrent: true, method: 'POST', data: { message } })
        .then(response => {
          const { body } = response
          store.dispatch(AddToCounter(todoId, 'messagesCount', 1))
          resolve(body)
        })
        .catch(reject)
    })
  },

  deleteMessage: (todoId, messageId) => {
    return new Promise((resolve, reject) => {
      const url = `${resourceHost}todo/message/${messageId}`
      return request(url, { auth: true, isConcurrent: true, method: 'DELETE' })
        .then(() => {
          store.dispatch(AddToCounter(todoId, 'messagesCount', -1))
          resolve()
        })
        .catch(reject)
    })
  },

  markMessagesAsRead: (todoId) => {
    try {
      const url = `${resourceHost}todo/${todoId}/messages/read`
      return request(url, { auth: true, isConcurrent: true, method: 'POST' })
    } catch (error) {
      console.error(error)
      throw error
    }
  },

  decline: (id) => {
    return new Promise((resolve, reject) => {
      return request(`${resourceHost}todo/${id}/status`, {
        auth: true,
        isConcurrent: true,
        method: 'PUT',
        data: { status: TodoStatusEnum.DECLINED },
      })
        .then((response) => {
          const { body: { data } } = response
          store.dispatch(updateItem(data))
          resolve(data)
        })
        .catch(reject)
    })
  },

  done: (id) => {
    return new Promise((resolve, reject) => {
      return request(`${resourceHost}todo/${id}/status`, {
        auth: true,
        isConcurrent: true,
        method: 'PUT',
        data: { status: TodoStatusEnum.DONE },
      })
        .then(response => {
          const { body: { data } } = response
          store.dispatch(updateItem(data))
          resolve(data)
        })
        .catch(reject)
    })
  },

  createRelationItem: (id, type) => {
    return { id, type }
  },

  link: (todoId, relations) => {
    return new Promise((resolve, reject) => {
      request(`${resourceHost}todo/${todoId}/link`, {
        method: 'PUT',
        auth: true,
        isConcurrent: true,
        data: { relations },
      })
        .then(response => {
          const { body: { data } } = response
          resolve(data)
        })
        .catch(reject)
    })
  },

  unlink: (todoId, relations) => {
    return new Promise((resolve, reject) => {
      request(`${resourceHost}todo/${todoId}/unlink`, {
        method: 'DELETE',
        auth: true,
        isConcurrent: true,
        data: { relations },
      })
        .then(response => {
          const { body: { data } } = response
          resolve(data)
        })
        .catch(reject)
    })
  },

  fetchDocumentInfo: documentId => {
    return new Promise((resolve, reject) => {
      return request(`${resourceHost}todoDocuments/${documentId}/meta`, {
        auth: true,
        isConcurrent: true,
        method: 'GET',
      })
        .then(response => {
          const { body: { data } } = response
          resolve(data)
        })
        .catch(reject)
    })
  },

  copyDocument: documentId => {
    return new Promise((resolve, reject) => {
      return request(`${resourceHost}todoDocuments/${documentId}/copy`, {
        auth: true,
        isConcurrent: true,
        method: 'PATCH',
      })
        .then(response => {
          const { body: { data } } = response
          resolve(data)
        })
        .catch(error => {
          handleUploadError({ response: error?.body ? JSON.stringify(error.body) : '{}' }, lang.defaultErrorMessage)
          reject(error)
        })
    })
  },

  removeDocument: documentId => {
    return new Promise((resolve, reject) => {
      return request(`${resourceHost}todoDocuments/${documentId}`, {
        auth: true,
        isConcurrent: true,
        method: 'DELETE',
      })
        .then(response => {
          const { body: { data } } = response
          resolve(data)
        })
        .catch(error => reject(error))
    })
  },

  sendSignatureInfoMail: async(id, emails) => {
    return request(`${resourceHost}todo/signature/send/${id}`, {
      method: 'POST',
      auth: true,
      isConcurrent: true,
      data: { emails },
    })
  },

  createSignatureTodo: async(data) => {
    try {
      const {
        message,
        dueDate,
        signers,
        tenantCollaborationId,
        file,
      } = data

      let body
      if (file.source === 'container') {
        const blob = await FileService.fetchFileBlobByUrl(file.url, tenantCollaborationId)
        const fileToUpload = new File([blob], file.name, {
          type: file.mimetype,
        })
        body = await uploadDocuments(fileToUpload, todoTypesEnum.SIGNATURE)
      } else {
        body = await uploadDocuments(file.file, todoTypesEnum.SIGNATURE)
      }

      const sanitizedSigners = signers.map((signer) => {
        const { id, multiSelectType, signatures, email } = signer
        return { signatures, id, type: multiSelectType, email }
      })

      const todoData = {
        content: message,
        dueToDate: moment(dueDate).endOf('day').format(apiDateFormat),
        tenantCollaborationId,
        todoDocumentIds: body?.body?.data?.[0]?.id ? [body?.body?.data?.[0]?.id] : [],
        fileIds: file.id ? [file.id] : [],
        signers: sanitizedSigners,
      }

      const requestData = { ...todoData, content: sanitizeContent(todoData.content) }
      const response = await request(`${resourceHost}todo/signature`, {
        method: 'POST',
        auth: true,
        isConcurrent: true,
        data: requestData,
      })
      const { body: { data: todo } } = response
      return todo
    } catch (error) {
      console.error(error)
      throw error
    }
  },

  markAsSeen: (todo, isSeen = true) => {
    const { id } = todo
    return new Promise((resolve, reject) => {
      request(`${resourceHost}todo/${id}/seen`, {
        method: 'PATCH',
        auth: true,
        isConcurrent: true,
        data: { isSeen },
      })
        .then(response => {
          const { body: { data } } = response
          store.dispatch(updateItem(data))
          resolve(data)
        })
        .catch(reject)
    })
  },

  TYPE_USER,
  TYPE_FILE,
  TYPE_SUPPLIER,
  TYPE_CUSTOMER,
  TYPE_DOCUMENT,
}

export default TodoService

export {
  TodoService,
  TODO_FILTER,
  TYPE_USER,
  TYPE_FILE,
  TYPE_SUPPLIER,
  TYPE_CUSTOMER,
}
